import axios from 'axios';

// Determine the base URL based on environment
const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || '/api';
  }
  return '/api';
};

// Create axios instance with base URL
const api = axios.create({
  baseURL: getBaseUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
// Endpoints where a 401 is a legitimate answer rather than an expired session:
// a failed sign-in, and a wrong current password when changing it. Redirecting
// on those reloads the page and destroys the error before the user can read it.
const EXPECTED_401_PATHS = ['/auth/login', '/auth/change-password'];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);

    const url = error.config?.url || '';
    const isExpected401 = EXPECTED_401_PATHS.some(p => url.includes(p));
    const alreadyOnLogin = window.location.pathname === '/login';

    // Only treat a 401 as an expired session when it comes from somewhere the
    // caller was supposed to be authenticated already.
    if (error.response?.status === 401 && !isExpected401 && !alreadyOnLogin) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const auth = {
  login: (data) => api.post('/auth/login', data),
  getCurrentUser: () => api.get('/auth/me'),
  // A user changing their own password must supply the current one.
  changePassword: (data) => api.post('/auth/change-password', data),
  getPasswordPolicy: () => api.get('/auth/password-policy')
};

// Admin API
export const admin = {
  listUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  listDIDs: () => api.get('/admin/dids'),
  createDID: (data) => api.post('/admin/dids', data),
  updateDID: (number, data) => api.put(`/admin/dids/${number}`, data),
  getAvailableDIDs: () => api.get('/admin/dids/available'),
  deleteDID: (number) => api.delete(`/admin/dids/${number}`),
  repairDIDAssignments: () => api.post('/admin/dids/repair'),

  // Organizations — a DID belongs to an organization and is shared by all of
  // its members.
  listOrganizations: () => api.get('/admin/organizations'),
  getOrganization: (id) => api.get(`/admin/organizations/${id}`),
  createOrganization: (data) => api.post('/admin/organizations', data),
  updateOrganization: (id, data) => api.put(`/admin/organizations/${id}`, data),
  deleteOrganization: (id) => api.delete(`/admin/organizations/${id}`),
  addOrganizationMember: (id, userId) => api.post(`/admin/organizations/${id}/members`, { userId }),
  removeOrganizationMember: (id, userId) => api.delete(`/admin/organizations/${id}/members/${userId}`),
  assignOrganizationDID: (id, didId) => api.post(`/admin/organizations/${id}/dids`, { didId }),
  unassignOrganizationDID: (id, didId) => api.delete(`/admin/organizations/${id}/dids/${didId}`)
};

// SMS API
export const sms = {
  getConversations: () => api.get('/sms/conversations'),
  getConversation: (partner, page = 1, limit = 50) => 
    api.get(`/sms/conversations/${partner}`, {
      params: { page, limit }
    }),
  markAsRead: (partner) => api.post(`/sms/conversations/${partner}/read`),
  deleteConversation: (partner) => api.delete(`/sms/conversations/${partner}`),
  send: (data) => api.post('/sms/messages', data),
  deleteMessage: (messageId) => api.delete(`/sms/messages/${messageId}`),
  updateMessageStatus: (messageId, data) => api.put(`/sms/messages/${messageId}/status`, data),

  // Upload MMS attachments, then pass the returned descriptors to send() as
  // `media`. Uploading separately keeps previews instant and lets a failed
  // send be retried without re-uploading.
  uploadMedia: (files, onProgress) => {
    const form = new FormData();
    Array.from(files).forEach(file => form.append('files', file));
    return api.post('/sms/media', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      }
    });
  }
};

// Contact endpoints
export const contacts = {
  list: () => api.get('/contacts'),
  create: (contact) => api.post('/contacts', contact),
  update: (id, contact) => api.put(`/contacts/${id}`, contact),
  delete: (id) => api.delete(`/contacts/${id}`),
  search: (query) => api.get('/contacts/search', { params: { query } })
};

export default api;
